import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { RootState } from "us.helper/types";
import { PlusOutlined, DeleteOutlined } from "us.icons";
import { AdditionalInfoDetailsProps } from "../../Interfaces";
import {
  getAdditionalInfoInitFormValues,
  convertValueToProperFormat,
  getValueDataType,
  getDefaultValue,
} from "../../Functions";
import { InformationRecord } from "us.collection/interfaces";
import { BMDActions, extendedField } from "us.collection/actions";
import { EntityType } from "../../Constants";
import { SaveInformation } from "us.collection/repository";
import { AdditionalInfoValidationSchema } from "../../Validations";
import { ExtendedFieldType } from "us.collection/constants";
import {
  getEntityDetail,
  getValueComponent,
  getExtendedFieldValueSource,
  getSelectedMetaDataInfo,
  getDynamicPlaceholderValue,
} from "us.common/functions";
import { AdditionalInfoDataTypes, ValueType } from "us.common/constants";

const {
  $Tooltip,
  $Button,
  $Row,
  $Col,
  $Select,
  $Popconfirm,
  $DynamicComponent,
} = Common.Components;

/**
 * @description - Additional information detail view component
 * @link Design Document - https://unicorn-solutions.atlassian.net/browse/UBX4-23262
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 04/11/2024
 */
const AdditionalInfoDetails: React.FC<
  AdditionalInfoDetailsProps & PropsFromRedux
> = (props) => {
  const { t } = useTranslation(["US.COLLECTION.COMMON"]);

  const {
    metaData,
    extendedFields,
    drawer,
    isFieldDeleting,
    infoMetaData,
    isFieldSaving,
    bmdDataTypeValues,
    onCancel,
    setAdditionalInformation,
    getInfoMetaData,
    getDataTypeValuesAction,
  } = props;

  const overLayElement: HTMLElement = document.getElementById(
    "hiddenOverlay"
  ) as HTMLElement;

  const { caseId, arId } = metaData.data ?? {};
  const [metaDataOptions, setMetaDataOptions] = useState(
    infoMetaData?.infoOptions
  );
  const { entityType } = useMemo(() => {
    return getEntityDetail(location.pathname, metaData.data);
  }, [caseId]);

  useEffect(() => {
    getInfoMetaData &&
      getInfoMetaData({
        category:
          entityType == EntityType.SUBCASE
            ? ExtendedFieldType.CASE
            : entityType,
      });
  }, []);

  const getOptionList = useCallback(
    (listKey: any) => {
      return getExtendedFieldValueSource(
        listKey,
        infoMetaData.infoOptions,
        bmdDataTypeValues?.data
      );
    },
    [bmdDataTypeValues?.data, infoMetaData]
  );

  useEffect(() => {
    setMetaDataOptions(infoMetaData?.infoOptions);
    if (!drawer?.isNewExtendedField) {
      const metaData = getSelectedMetaDataInfo(
        drawer?.fieldName,
        infoMetaData.infoOptions
      );
      metaData?.valueType == ValueType.SP &&
        getDataTypeValuesAction &&
        getDataTypeValuesAction({
          dataTypeId: metaData?.valueTypeId,
        });
    }
  }, [drawer, infoMetaData]);

  const initialFormValues = useMemo(() => {
    return getAdditionalInfoInitFormValues(
      drawer?.infoId,
      extendedFields?.data ?? []
    );
  }, [drawer?.infoId, extendedFields?.data]);

  /**
   * @Description Add new information
   */
  const handleAdd = (setValues: any) => {
    setValues(
      (preValues: any) => ({
        ...preValues,
        informationList: [
          ...preValues?.informationList,
          { key: "", value: "" },
        ],
      }),
      false
    );
  };
  /**
   * @Description Delete information
   */
  const handleDelete = (
    informationData: any[],
    infoIndex: number,
    setFieldValue: any
  ) => {
    let newInformationList = [...informationData];
    if (newInformationList?.length > 1) {
      newInformationList?.splice(infoIndex, 1);
      setFieldValue("informationList", newInformationList);
    }
  };

  /**
   * @description Handle submit button action
   * @param {any} values
   */
  const handleSubmit = (values: any) => {
    const saveParams = values.informationList.map((infoItem: any) =>
      SaveInformation.call({
        ...values,
        metaData: infoMetaData.infoOptions.filter(
          (option: any) => option?.label === infoItem.key
        ),
        recordId: drawer?.infoId,
        entityId: entityType === ExtendedFieldType.AR ? arId : caseId,
        informationItem: infoItem,
      })
    );

    setAdditionalInformation &&
      setAdditionalInformation({
        category:
          entityType == EntityType.SUBCASE
            ? ExtendedFieldType.CASE
            : entityType,
        request: saveParams,
      });
    onCancel();
  };

  /**
   * @description Handle submit button action
   * @param {any} value
   * @param {any} option
   * @param {any} setFieldValue
   * @param {any} setFieldTouched
   * @param {any} index
   */
  const handleSelect = (
    value: any,
    option: any,
    setFieldValue: any,
    setFieldTouched: any,
    index: any
  ) => {
    const updatedOptions = metaDataOptions.filter(
      (metaDataOption: any) => metaDataOption.label !== value
    );
    setMetaDataOptions(updatedOptions);
    const metaData = getSelectedMetaDataInfo(value, infoMetaData.infoOptions);
    metaData?.valueType == ValueType.SP &&
      getDataTypeValuesAction &&
      getDataTypeValuesAction({
        dataTypeId: metaData?.valueTypeId,
      });
    setFieldValue(
      `informationList[${index}].value`,
      getDefaultValue(option?.value, infoMetaData.infoOptions)
    );
    setTimeout(() => {
      setFieldTouched(`informationList[${index}].key`, true);
    }, 50);
  };

  /**
   * Retrieves the placeholder value for a given key based on its data type and metadata.
   *
   * @param key - The key for which the placeholder value is to be determined.
   * @returns The translated placeholder value corresponding to the key's data type.
   *
   * The function determines the data type of the key using `getValueDataType` and
   * retrieves a dynamic placeholder value using `getDynamicPlaceholderValue`. The
   * placeholder is then translated using the `t` function.
   */
  const getPlaceholderValue = (key: any) => {
    const dataType = getValueDataType(key, infoMetaData.infoOptions);
    const placeholder = getDynamicPlaceholderValue(dataType);
    return t(placeholder);
  };

  return (
    <Formik
      initialValues={initialFormValues}
      enableReinitialize
      validateOnMount
      validateOnChange
      validateOnBlur
      validationSchema={AdditionalInfoValidationSchema(
        extendedFields?.data,
        drawer?.isNewExtendedField
      )}
      onSubmit={handleSubmit}
    >
      {({
        values,
        isSubmitting,
        isValid,
        handleSubmit,
        setValues,
        setFieldValue,
        setFieldTouched,
        dirty,
      }) => (
        <div className="additional-info" id="hiddenOverlay">
          <label className="ant-form-item-required">
            {t("US.COLLECTION.COMMON:EXTENDED_FIELD.FIELD")}
          </label>
          {values?.informationList?.map(
            (infoRecord: InformationRecord, index: number) => (
              <div className="info-item-container" key={index}>
                <div className="info-content-items">
                  <$Row gutter={20} className="align-content-center w-100">
                    <$Col span={10}>
                      <$Tooltip placement="topLeft" title={infoRecord?.key}>
                        <$Select
                          name={`informationList[${index}].key`}
                          formitem={{}}
                          placeholder={t(
                            "US.COLLECTION.COMMON:EXTENDED_FIELD.HEADER"
                          )}
                          options={metaDataOptions}
                          size="small"
                          allOption={false}
                          virtual={false}
                          disabled={
                            infoMetaData.isLoading ||
                            !drawer?.isNewExtendedField
                          }
                          onSearchBy={["label"]}
                          getPopupContainer={() => overLayElement}
                          className="w-100"
                          onSelect={(value: any, option: any) => {
                            handleSelect(
                              value,
                              option,
                              setFieldValue,
                              setFieldTouched,
                              index
                            );
                          }}
                        />
                      </$Tooltip>
                    </$Col>
                    <$Col span={14}>
                      <$Tooltip
                        placement="topLeft"
                        title={
                          getValueDataType(
                            values?.informationList[index].key,
                            infoMetaData.infoOptions
                          ) != AdditionalInfoDataTypes.DATE_TIME
                            ? infoRecord?.value
                            : ""
                        }
                      >
                        <span>
                          <$DynamicComponent
                            name={`informationList[${index}].value`}
                            component={getValueComponent(
                              values?.informationList[index].key,
                              infoMetaData.infoOptions
                            )}
                            value={convertValueToProperFormat(
                              values?.informationList[index],
                              infoMetaData.infoOptions
                            )}
                            className={
                              getValueDataType(
                                values?.informationList[index].key,
                                infoMetaData.infoOptions
                              ) != AdditionalInfoDataTypes.BOOLEAN
                                ? "w-100"
                                : ""
                            }
                            placeholder={getPlaceholderValue(
                              values?.informationList[index].key
                            )}
                            required
                            tabIndex={4}
                            options={getOptionList(
                              values?.informationList[index].key
                            )}
                            allOption={false}
                            virtual={false}
                            onSearchBy={["label"]}
                            disabled={
                              infoMetaData?.isLoading ||
                              bmdDataTypeValues?.isLoading
                            }
                            getPopupContainer={() =>
                              document?.getElementById(
                                "hiddenOverlay"
                              ) as HTMLElement
                            }
                            maxLength={
                              200
                            }
                            precision={
                              getValueDataType(
                                values?.informationList[index].key,
                                infoMetaData.infoOptions
                              ) === AdditionalInfoDataTypes.INT
                                ? 0
                                : undefined
                            }
                          />
                        </span>
                      </$Tooltip>
                    </$Col>
                  </$Row>
                </div>
                {drawer?.isNewExtendedField && (
                  <div
                    className="d-flex align-items-center justify-content-end"
                    style={{ width: "90px" }}
                  >
                    <div className="d-flex h-100 align-items-center">
                      <$Popconfirm
                        title={t("COMMON.SURE_TO_DELETE_?")}
                        placement="topLeft"
                        onConfirm={() => {
                          handleDelete(
                            values?.informationList,
                            index,
                            setFieldValue
                          );
                        }}
                      >
                        <$Tooltip
                          placement="topLeft"
                          title={t(
                            "US.COLLECTION.COMMON:EXTENDED_FIELD.DELETE_INFORMATION"
                          )}
                        >
                          <div
                            className="info-delete-btn"
                            hidden={values?.informationList?.length == 1}
                          >
                            <DeleteOutlined className="text-error" />
                          </div>
                        </$Tooltip>
                      </$Popconfirm>
                      {values?.informationList?.length - 1 == index && (
                        <$Tooltip
                          placement="topLeft"
                          title={t(
                            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.ADD_NEW_INFORMATION"
                          )}
                        >
                          <div
                            className="add-btn"
                            onClick={() => handleAdd(setValues)}
                          >
                            <PlusOutlined className="add-btn-text" />
                          </div>
                        </$Tooltip>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          )}
          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Popconfirm
                title={t("COMMON.SURE_TO_PROCEED_?")}
                placement="topLeft"
                data-testid="pop-confirm"
                okButtonProps={{
                  dataTestid: "pop-confirm-ok",
                }}
                onConfirm={() => handleSubmit()}
                okText={t("COMMON.YES")}
                cancelText={t("COMMON.NO")}
                disabled={isFieldSaving || isSubmitting || !isValid || !dirty}
              >
                <$Button
                  className="ml-3 mr-2"
                  type="primary"
                  data-testid="save-btn"
                  disabled={isFieldSaving || isSubmitting || !isValid || !dirty}
                  loading={isFieldSaving || isSubmitting}
                >
                  {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                </$Button>
              </$Popconfirm>
              <$Button onClick={() => onCancel()}>
                {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
              </$Button>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};

const mapState = (state: RootState) => {
  const { common, domainView, extendedField, bmd } = state;
  const { metaData } = domainView;
  const { bmdDataTypeValues } = bmd;
  const {
    extendedFields,
    drawer,
    metaData: infoMetaData,
    isFieldDeleting,
    isFieldSaving,
  } = extendedField;
  return {
    metaData,
    currentLanguage: common.currentLanguage,
    extendedFields,
    drawer,
    isFieldDeleting,
    infoMetaData,
    isFieldSaving,
    bmdDataTypeValues,
  };
};

const { metaData, extendedFieldData } = extendedField;
const { bmdDataTypeValues } = BMDActions;

const mapDispatch = {
  setAdditionalInformation: extendedFieldData.save,
  getInfoMetaData: metaData.get,
  getDataTypeValuesAction: bmdDataTypeValues.get,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AdditionalInfoDetails);
